import { useState } from 'react'
import dayjs from 'dayjs'
import { Modal, useModals } from 'almanac-ui/Modal'
import { Button } from 'blaze-ui/Button'
import { Text } from 'blaze-ui/Text/Text'
import { useCampaignOptions } from 'hooks/queries/campaignOptions/useCampaignOptions'
import { useUpdateBrandPlanScheduleContent } from 'hooks/queries/schedules/useUpdateBrandPlanScheduleContent'
import { collectResults } from 'utils/query'
import { MoveToCampaignTable } from './MoveToCampaignTable'
import Styles from './MoveToCampaignModal.module.scss'

interface Props {
  brandPlanId: number
  sourceScheduleId: number
  contentId: number
  fileKey: string
}

export const MoveToCampaignModal = ({ brandPlanId, sourceScheduleId, contentId, fileKey }: Props) => {
  const { closeModal } = useModals()
  const [selectedScheduleId, setSelectedScheduleId] = useState<number | undefined>(undefined)

  const schedulesQuery = useCampaignOptions(
    { brandPlanId, start_date: dayjs().format('YYYY-MM-DD') },
    { enabled: !!brandPlanId },
  )
  const campaigns = collectResults(schedulesQuery, (d) => d.campaign_options).filter((c) => c.id !== sourceScheduleId)

  const {
    mutate: updateContent,
    isLoading: isMoving,
    isError,
  } = useUpdateBrandPlanScheduleContent({ scheduleId: sourceScheduleId, contentId, fileKey })

  const handleMove = () => {
    if (!selectedScheduleId) return
    updateContent({ brand_plan_schedule_id: selectedScheduleId }, { onSuccess: () => closeModal() })
  }

  const showEmptyState = !schedulesQuery.isLoading && campaigns.length === 0 && !schedulesQuery.hasNextPage

  return (
    <Modal.Root size="md">
      <Modal.Header title="Move this post to a campaign" />
      <Modal.Content className={Styles.body}>
        {showEmptyState ? (
          <div className={Styles.empty} role="status">
            <Text variant="secondary" color="var(--dark-60)">
              No campaigns available to move this post to.
            </Text>
          </div>
        ) : (
          <MoveToCampaignTable
            campaigns={campaigns}
            selectedScheduleId={selectedScheduleId}
            onSelect={setSelectedScheduleId}
            isLoading={schedulesQuery.isLoading}
          />
        )}
        {schedulesQuery.hasNextPage && (
          <Button
            className={Styles.loadMore}
            variant="tertiary"
            size="sm"
            isDisabled={schedulesQuery.isFetchingNextPage}
            onPress={() => schedulesQuery.fetchNextPage()}
          >
            {schedulesQuery.isFetchingNextPage ? 'Loading…' : 'Load more'}
          </Button>
        )}
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Modal.FooterButton variant="tertiary" onPress={closeModal}>
            Cancel
          </Modal.FooterButton>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          {isError && (
            <span role="alert" className={Styles.error}>
              Couldn&apos;t move post. Try again.
            </span>
          )}
          <Modal.FooterButton onPress={handleMove} isDisabled={!selectedScheduleId || isMoving}>
            {isMoving ? 'Moving…' : 'Move Post'}
          </Modal.FooterButton>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}
